"use client";

import React from "react";

/**
 * useFilterSearch Hook
 *
 * Custom hook that encapsulates all the state management and business logic
 * for the FilterSearch component. This separation allows for:
 * - Easier testing of logic without UI
 * - Reusability of filter logic in different UI implementations
 * - Cleaner component code focused on presentation
 */

import { useState, useRef, useEffect, useCallback, type KeyboardEvent } from "react";
// import type { Dayjs } from "dayjs";
import type { Moment } from "moment";
import type { FilterOption, ActiveFilter, SubOption, DropdownMode } from "../types";

/**
 * Configuration options for the useFilterSearch hook
 */
interface UseFilterSearchOptions {
  /** Available filter options */
  filterOptions: FilterOption[];
  /** Callback when filters change */
  onSearch?: (filters: ActiveFilter[]) => void;
  /** Filter keys for default search behavior */
  defaultSearchFilters?: string[];
  /** Pre-applied filters on mount */
  initialFilters?: ActiveFilter[];
}

/**
 * Return type for the useFilterSearch hook
 */
interface UseFilterSearchReturn {
  // State values
  activeFilters: ActiveFilter[];
  currentFilter: FilterOption | null;
  currentSubOption: SubOption | null;
  inputValue: string;
  showDropdown: boolean;
  dropdownMode: DropdownMode;
  isFocused: boolean;
  datePickerOpen: boolean;
  selectedDate: Moment | null;
  availableFilters: FilterOption[];
  isDateFilter: boolean;

  // Refs
  containerRef: React.RefObject<HTMLDivElement>;
  inputRef: React.RefObject<HTMLInputElement>;

  // Handlers
  handleContainerClick: () => void;
  handleFilterSelect: (filter: FilterOption) => void;
  handleSubOptionSelect: (subOption: SubOption) => void;
  handleInputChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleKeyDown: (e: KeyboardEvent<HTMLInputElement>) => void;
  handleRemoveFilter: (key: string) => void;
  handleClearCurrentFilter: () => void;
  handleClickAway: () => void;
  handleDateChange: (date: Moment | null) => void;
  handleDateAccept: (date: Moment | null) => void;
  setIsFocused: (focused: boolean) => void;

  // Utility functions
  getChipLabel: (filter: ActiveFilter) => string;
  getCurrentChipLabel: () => string;
  canEnterValue: () => boolean;
}

/**
 * Custom hook for managing FilterSearch state and logic.
 *
 * @param options - Configuration options for the hook
 * @returns Object containing state, refs, handlers, and utility functions
 *
 * @example
 * const {
 *   activeFilters,
 *   handleFilterSelect,
 *   handleKeyDown,
 *   ...rest
 * } = useFilterSearch({
 *   filterOptions: myFilters,
 *   onSearch: (filters) => console.log(filters)
 * })
 */
export function useFilterSearch({
  filterOptions,
  onSearch,
  defaultSearchFilters,
  initialFilters,
}: UseFilterSearchOptions): UseFilterSearchReturn {
  // ============================================
  // State Management
  // ============================================

  /** Array of currently applied filters */
  const [activeFilters, setActiveFilters] = useState<ActiveFilter[]>(initialFilters || []);

  /** Currently selected filter (before value is entered) */
  const [currentFilter, setCurrentFilter] = useState<FilterOption | null>(null);

  /** Currently selected sub-option (for filters with conditions) */
  const [currentSubOption, setCurrentSubOption] = useState<SubOption | null>(null);

  /** Current value in the text input */
  const [inputValue, setInputValue] = useState("");

  /** Whether the dropdown menu is visible */
  const [showDropdown, setShowDropdown] = useState(false);

  /** Current mode of the dropdown (showing filters or sub-options) */
  const [dropdownMode, setDropdownMode] = useState<DropdownMode>("filters");

  /** Whether the input container is focused */
  const [isFocused, setIsFocused] = useState(false);

  /** Whether the date picker is open (for date filters) */
  const [datePickerOpen, setDatePickerOpen] = useState(false);

  /** Currently selected date in the date picker */
  const [selectedDate, setSelectedDate] = useState<Moment | null>(null);

  /** Flag to track if initial filters have been notified to parent */
  const [isInitialized, setIsInitialized] = useState(false);

  // ============================================
  // Refs
  // ============================================

  /** Reference to the main container element (for positioning dropdowns) */
  const containerRef = useRef<HTMLDivElement>(null);

  /** Reference to the text input element (for focus management) */
  const inputRef = useRef<HTMLInputElement>(null);

  // ============================================
  // Computed Values
  // ============================================

  /**
   * Filters that are available for selection (not already active)
   */
  const availableFilters = filterOptions.filter(
    (filter) => !activeFilters.some((active) => active.key === filter.key)
  );

  /**
   * Whether the current filter is a date type filter
   */
  const isDateFilter = currentFilter?.type === "date";

  // ============================================
  // Effects
  // ============================================

  /**
   * Notify parent of initial filters on first render.
   * This ensures the parent component knows about pre-applied filters.
   */
  useEffect(() => {
    if (!isInitialized && initialFilters && initialFilters.length > 0) {
      onSearch?.(initialFilters);
      setIsInitialized(true);
    }
  }, [initialFilters, isInitialized, onSearch]);

  /**
   * Auto-show dropdown when input is focused and no filter is selected.
   * Improves UX by immediately showing available options.
   */
  useEffect(() => {
    if (isFocused && !currentFilter && availableFilters.length > 0 && !showDropdown) {
      setShowDropdown(true);
      setDropdownMode("filters");
    }
  }, [isFocused, currentFilter, availableFilters.length, showDropdown]);

  // ============================================
  // Utility Functions
  // ============================================

  /**
   * Generates the display label for an active filter chip.
   *
   * @param filter - The active filter to generate label for
   * @returns Formatted string for chip display
   *
   * @example
   * // For immediate apply: "Plans=free"
   * // For sub-option with value: "Price more than 100"
   * // For simple filter: "Name=John"
   */
  const getChipLabel = useCallback((filter: ActiveFilter): string => {
    if (filter.subOption) {
      // Immediate apply filters show "Label=value" format
      if (filter.subOption.applyImmediately) {
        return `${filter.label}=${filter.subOption.displayLabel}`;
      }
      // Sub-option filters show "Label condition value" format
      return `${filter.label} ${filter.subOption.displayLabel} ${filter.value}`;
    }
    // Simple filters show "Label=value" format
    return `${filter.label}=${filter.value}`;
  }, []);

  /**
   * Generates the display label for the current (incomplete) filter chip.
   * Shows different states based on selection progress.
   *
   * @returns Formatted string for current filter chip
   */
  const getCurrentChipLabel = useCallback((): string => {
    if (!currentFilter) return "";

    // Sub-option selected - show filter with condition
    if (currentSubOption) {
      return `${currentFilter.label} ${currentSubOption.displayLabel}`;
    }

    // Filter has sub-options but none selected yet - show ellipsis
    if (currentFilter.subOptions && currentFilter.subOptions.length > 0) {
      return `${currentFilter.label}...`;
    }

    // Simple filter - show with equals sign ready for input
    return `${currentFilter.label}=`;
  }, [currentFilter, currentSubOption]);

  /**
   * Determines if the user can enter a value in the text input.
   * Prevents input when date picker should be used or sub-option is required.
   *
   * @returns Whether value input is allowed
   */
  const canEnterValue = useCallback((): boolean => {
    if (!currentFilter) return false;

    // Date filters use date picker, not text input
    if (currentFilter.type === "date") return false;

    // Filters with sub-options require sub-option selection first
    if (currentFilter.subOptions && currentFilter.subOptions.length > 0) {
      return currentSubOption !== null;
    }

    return true;
  }, [currentFilter, currentSubOption]);

  // ============================================
  // Event Handlers
  // ============================================

  /**
   * Handles click on the main container.
   * Opens dropdown if no filter is selected and filters are available.
   */
  const handleContainerClick = useCallback(() => {
    if (!currentFilter && availableFilters.length > 0) {
      setShowDropdown(true);
      setDropdownMode("filters");
    }
    inputRef.current?.focus();
  }, [currentFilter, availableFilters.length]);

  /**
   * Handles selection of a filter from the dropdown.
   * Routes to appropriate next step based on filter type.
   *
   * @param filter - The selected filter option
   */
  const handleFilterSelect = useCallback((filter: FilterOption) => {
    setCurrentFilter(filter);

    if (filter.subOptions && filter.subOptions.length > 0) {
      // Filter has sub-options - show them
      setDropdownMode("subOptions");
      setShowDropdown(true);
    } else if (filter.type === "date") {
      // Date filter - open date picker
      setShowDropdown(false);
      setDatePickerOpen(true);
      setSelectedDate(null);
    } else {
      // Simple filter - close dropdown for value input
      setShowDropdown(false);
    }

    setInputValue("");
    setTimeout(() => inputRef.current?.focus(), 0);
  }, []);

  /**
   * Handles selection of a sub-option.
   * Either applies filter immediately or waits for value input.
   *
   * @param subOption - The selected sub-option
   */
  const handleSubOptionSelect = useCallback(
    (subOption: SubOption) => {
      // Immediate apply sub-options don't need value input
      if (subOption.applyImmediately && currentFilter) {
        const newFilter: ActiveFilter = {
          key: currentFilter.key,
          label: currentFilter.label,
          value: subOption.displayLabel,
          subOption,
        };
        const updatedFilters = [...activeFilters, newFilter];
        setActiveFilters(updatedFilters);
        setCurrentFilter(null);
        setCurrentSubOption(null);
        setShowDropdown(false);
        setInputValue("");
        onSearch?.(updatedFilters);
        return;
      }

      // Regular sub-option - wait for value input
      setCurrentSubOption(subOption);
      setShowDropdown(false);
      setInputValue("");
      setTimeout(() => inputRef.current?.focus(), 0);
    },
    [currentFilter, activeFilters, onSearch]
  );

  /**
   * Handles text input change.
   *
   * @param e - Input change event
   */
  const handleInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(e.target.value);
  }, []);

  /**
   * Handles keyboard events in the text input.
   * Supports Enter (apply), Backspace (remove), and Escape (cancel).
   *
   * @param e - Keyboard event
   */
  const handleKeyDown = useCallback(
    (e: KeyboardEvent<HTMLInputElement>) => {
      // Enter with current filter selected - apply the filter
      if (e.key === "Enter" && currentFilter && inputValue.trim() && canEnterValue()) {
        const newFilter: ActiveFilter = {
          key: currentFilter.key,
          label: currentFilter.label,
          value: inputValue.trim(),
          subOption: currentSubOption || undefined,
        };
        const updatedFilters = [...activeFilters, newFilter];
        setActiveFilters(updatedFilters);
        setCurrentFilter(null);
        setCurrentSubOption(null);
        setInputValue("");
        onSearch?.(updatedFilters);
        return;
      }

      // Enter without filter - apply to default search filters
      if (
        e.key === "Enter" &&
        !currentFilter &&
        inputValue.trim() &&
        defaultSearchFilters &&
        defaultSearchFilters.length > 0
      ) {
        const newFilters: ActiveFilter[] = [];

        for (const filterKey of defaultSearchFilters) {
          // Skip already active filters
          if (activeFilters.some((f) => f.key === filterKey)) continue;

          const filterOption = filterOptions.find((f) => f.key === filterKey);
          // Only apply to simple text filters
          if (filterOption && !filterOption.subOptions && filterOption.type !== "date") {
            newFilters.push({
              key: filterOption.key,
              label: filterOption.label,
              value: inputValue.trim(),
            });
          }
        }

        if (newFilters.length > 0) {
          const updatedFilters = [...activeFilters, ...newFilters];
          setActiveFilters(updatedFilters);
          setInputValue("");
          onSearch?.(updatedFilters);
        }
        return;
      }

      // Backspace with current filter but no value - clear current filter
      if (e.key === "Backspace" && !inputValue && currentFilter) {
        setCurrentFilter(null);
        setCurrentSubOption(null);
        setShowDropdown(true);
        setDropdownMode("filters");
        return;
      }

      // Backspace with no current filter - remove last active filter
      if (e.key === "Backspace" && !inputValue && !currentFilter && activeFilters.length > 0) {
        const updatedFilters = activeFilters.slice(0, -1);
        setActiveFilters(updatedFilters);
        onSearch?.(updatedFilters);
        return;
      }

      // Escape - cancel current selection
      if (e.key === "Escape") {
        setCurrentFilter(null);
        setCurrentSubOption(null);
        setShowDropdown(false);
        setDropdownMode("filters");
        setInputValue("");
        setDatePickerOpen(false);
        setSelectedDate(null);
      }
    },
    [
      currentFilter,
      currentSubOption,
      inputValue,
      activeFilters,
      defaultSearchFilters,
      filterOptions,
      onSearch,
      canEnterValue,
    ]
  );

  /**
   * Handles removal of an active filter by its key.
   *
   * @param key - Key of the filter to remove
   */
  const handleRemoveFilter = useCallback(
    (key: string) => {
      const updatedFilters = activeFilters.filter((f) => f.key !== key);
      setActiveFilters(updatedFilters);
      onSearch?.(updatedFilters);
    },
    [activeFilters, onSearch]
  );

  /**
   * Clears the current filter selection (before value is entered).
   * Returns to filter selection state.
   */
  const handleClearCurrentFilter = useCallback(() => {
    setCurrentFilter(null);
    setCurrentSubOption(null);
    setInputValue("");
    setShowDropdown(true);
    setDropdownMode("filters");
    inputRef.current?.focus();
  }, []);

  /**
   * Handles click outside the component.
   * Closes dropdown and resets incomplete selections.
   */
  const handleClickAway = useCallback(() => {
    // Don't close if date picker is active
    if (datePickerOpen) return;

    setShowDropdown(false);
    // Clear incomplete filter selection (except date filters)
    if (currentFilter && !inputValue.trim() && !isDateFilter) {
      setCurrentFilter(null);
      setCurrentSubOption(null);
    }
    setDropdownMode("filters");
    setIsFocused(false);
  }, [datePickerOpen, currentFilter, inputValue, isDateFilter]);

  /**
   * Handles date selection in the date picker.
   *
   * @param date - Selected date or null
   */
  const handleDateChange = useCallback((date: Moment | null) => {
    setSelectedDate(date);
  }, []);

  /**
   * Handles date acceptance in the date picker.
   * Creates and applies the date filter.
   *
   * @param date - Accepted date
   */
  const handleDateAccept = useCallback(
    (date: Moment | null) => {
      if (date && currentFilter) {
        const formattedDate = date.format("DD/MM/YYYY");
        const newFilter: ActiveFilter = {
          key: currentFilter.key,
          label: currentFilter.label,
          value: formattedDate,
        };
        const updatedFilters = [...activeFilters, newFilter];
        setActiveFilters(updatedFilters);
        setCurrentFilter(null);
        setSelectedDate(null);
        setDatePickerOpen(false);
        onSearch?.(updatedFilters);
      }
    },
    [currentFilter, activeFilters, onSearch]
  );

  // ============================================
  // Return Hook Interface
  // ============================================

  return {
    // State
    activeFilters,
    currentFilter,
    currentSubOption,
    inputValue,
    showDropdown,
    dropdownMode,
    isFocused,
    datePickerOpen,
    selectedDate,
    availableFilters,
    isDateFilter,

    // Refs
    containerRef: containerRef as React.RefObject<HTMLDivElement>,
    inputRef: inputRef as React.RefObject<HTMLInputElement>,

    // Handlers
    handleContainerClick,
    handleFilterSelect,
    handleSubOptionSelect,
    handleInputChange,
    handleKeyDown,
    handleRemoveFilter,
    handleClearCurrentFilter,
    handleClickAway,
    handleDateChange,
    handleDateAccept,
    setIsFocused,

    // Utilities
    getChipLabel,
    getCurrentChipLabel,
    canEnterValue,
  };
}
